import { useState, useEffect, useCallback, Fragment } from 'react';
import { fetchInvoices } from '../../services/invoiceApi';
import type { InvoiceApiRecord, InvoiceStatus, InvoiceListMeta } from '../../services/invoiceApi';
import type { ApiError } from '../../services/api';
import RupeeIcon from '../icons/RupeeIcon';
import './StudentFeeInvoices.css';

interface StudentFeeInvoicesProps {
    studentId: string;
    studentName: string;
    onClose: () => void;
    /** Called when the user clicks "Collect fee" for an invoice. */
    onCollect: (invoice: InvoiceApiRecord) => void;
    /** Called to collect for the student rather than one invoice — the desk opens on the
        month grid, where several months (billed or not) are paid with one amount. */
    onCollectMonths: () => void;
}

function formatCurrency(n: number): string {
    return '₹' + n.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function formatDate(iso: string | null): string {
    if (!iso) return '—';
    return new Date(iso + 'T00:00:00').toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
}

const STATUS_LABELS: Record<InvoiceStatus, string> = {
    paid: 'Paid', partial: 'Partial', overdue: 'Overdue', published: 'Published', draft: 'Draft', void: 'Void',
};

const STATUS_FILTERS: { value: InvoiceStatus | ''; label: string }[] = [
    { value: '', label: 'All Status' },
    { value: 'published', label: 'Published' },
    { value: 'partial', label: 'Partial' },
    { value: 'overdue', label: 'Overdue' },
    { value: 'paid', label: 'Paid' },
    { value: 'draft', label: 'Draft' },
];

const PAGE_SIZE = 15;

export default function StudentFeeInvoices({ studentId, studentName, onClose, onCollect, onCollectMonths }: StudentFeeInvoicesProps) {
    const [invoices, setInvoices] = useState<InvoiceApiRecord[]>([]);
    const [meta, setMeta] = useState<InvoiceListMeta>({ current_page: 1, last_page: 1, per_page: PAGE_SIZE, total: 0 });
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [statusFilter, setStatusFilter] = useState<InvoiceStatus | ''>('');
    const [page, setPage] = useState(1);
    const [expanded, setExpanded] = useState<Set<string>>(new Set());

    const toggleExpand = (id: string) => setExpanded((prev) => {
        const next = new Set(prev);
        if (next.has(id)) next.delete(id); else next.add(id);
        return next;
    });

    const load = useCallback(async () => {
        setLoading(true);
        setError('');
        try {
            const res = await fetchInvoices({ student_id: studentId, status: statusFilter || undefined, per_page: PAGE_SIZE, page });
            setInvoices(res.data);
            setMeta(res.meta ?? { current_page: page, last_page: 1, per_page: PAGE_SIZE, total: res.data.length });
        } catch (err) {
            setError((err as ApiError).message || 'Failed to load invoices.');
        } finally {
            setLoading(false);
        }
    }, [studentId, statusFilter, page]);

    useEffect(() => { load(); }, [load]);

    /* Reset to page 1 when the status filter changes */
    useEffect(() => { setPage(1); }, [statusFilter]);

    /* Close on Escape */
    useEffect(() => {
        const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
        document.addEventListener('keydown', onKey);
        return () => document.removeEventListener('keydown', onKey);
    }, [onClose]);

    return (
        <>
            <div className="sfi__backdrop" onClick={onClose} />
            <div className="sfi__modal" role="dialog" aria-modal="true" aria-label={`Invoices for ${studentName}`}>
                <div className="sfi__header">
                    <div>
                        <h2 className="sfi__title">Fee Invoices</h2>
                        <p className="sfi__subtitle">{studentName}</p>
                    </div>
                    <button className="sfi__close" onClick={onClose} title="Close" aria-label="Close">
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>
                    </button>
                </div>

                <div className="sfi__toolbar">
                    <select className="sfi__filter" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value as InvoiceStatus | '')}>
                        {STATUS_FILTERS.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
                    </select>
                    <span className="sfi__count">{meta.total} invoice{meta.total !== 1 ? 's' : ''}</span>
                    {/* A month the student was never billed for has no invoice to collect against,
                        so it can't be reached from this list — this is the way in. */}
                    <button className="sfi__months-btn" onClick={onCollectMonths} title="Collect fees by month, including months not yet billed">
                        <RupeeIcon size={14} />
                        Collect monthly fees
                    </button>
                </div>

                <div className="sfi__body">
                    {error && (
                        <div className="sfi__error" role="alert">
                            <span>{error}</span>
                            <button className="sfi__error-retry" onClick={load}>Retry</button>
                        </div>
                    )}

                    {loading ? (
                        <div className="sfi__loading">
                            <svg className="sfi__spinner" width="24" height="24" viewBox="0 0 24 24" fill="none"><circle cx="12" cy="12" r="10" strokeWidth="3" /></svg>
                            <span>Loading invoices…</span>
                        </div>
                    ) : invoices.length === 0 ? (
                        <div className="sfi__empty">
                            <h3 className="sfi__empty-title">No invoices found</h3>
                            <p className="sfi__empty-text">{statusFilter ? 'No invoices match this status.' : 'No invoices have been generated for this student yet.'}</p>
                        </div>
                    ) : (
                        <div className="sfi__table-wrap">
                            <table className="sfi__table">
                                <thead>
                                    <tr>
                                        <th className="sfi__th">Invoice No.</th>
                                        <th className="sfi__th">Title</th>
                                        <th className="sfi__th sfi__th--amount">Total</th>
                                        <th className="sfi__th sfi__th--amount">Paid</th>
                                        <th className="sfi__th sfi__th--amount">Balance</th>
                                        <th className="sfi__th sfi__th--center">Status</th>
                                        <th className="sfi__th">Due</th>
                                        <th className="sfi__th sfi__th--action"></th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {invoices.map((inv) => {
                                        const balance = parseFloat(inv.balance_amount);
                                        const collectible = balance > 0 && inv.status !== 'draft' && inv.status !== 'void';
                                        const isExpanded = expanded.has(inv.id);
                                        const items = inv.items ?? [];
                                        return (
                                            <Fragment key={inv.id}>
                                                <tr className={`sfi__row ${isExpanded ? 'sfi__row--expanded' : ''}`}>
                                                    <td className="sfi__td sfi__td--inv">
                                                        <button
                                                            className="sfi__expand"
                                                            onClick={() => toggleExpand(inv.id)}
                                                            aria-expanded={isExpanded}
                                                            title={isExpanded ? 'Hide line items' : 'Show line items'}
                                                        >
                                                            <svg className={`sfi__chevron ${isExpanded ? 'sfi__chevron--open' : ''}`} width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 18 15 12 9 6" /></svg>
                                                        </button>
                                                        <span className="sfi__inv-num">{inv.invoice_number}</span>
                                                    </td>
                                                    <td className="sfi__td">{inv.title}</td>
                                                    <td className="sfi__td sfi__td--amount">{formatCurrency(parseFloat(inv.total_amount))}</td>
                                                    <td className="sfi__td sfi__td--amount sfi__td--paid">{formatCurrency(parseFloat(inv.paid_amount))}</td>
                                                    <td className="sfi__td sfi__td--amount sfi__td--balance">{formatCurrency(balance)}</td>
                                                    <td className="sfi__td sfi__td--center">
                                                        <span className={`sfi__badge sfi__badge--${inv.status}`}>{STATUS_LABELS[inv.status] ?? inv.status}</span>
                                                    </td>
                                                    <td className="sfi__td sfi__td--date">{formatDate(inv.due_date)}</td>
                                                    <td className="sfi__td sfi__td--action">
                                                        <button
                                                            className="sfi__collect-btn"
                                                            onClick={() => onCollect(inv)}
                                                            disabled={!collectible}
                                                            title={collectible ? 'Collect fee for this invoice' : 'Nothing to collect'}
                                                        >
                                                            <RupeeIcon size={14} />
                                                            Collect fee
                                                        </button>
                                                    </td>
                                                </tr>
                                                {isExpanded && (
                                                    <tr className="sfi__detail-row">
                                                        <td className="sfi__detail-cell" colSpan={8}>
                                                            {items.length === 0 ? (
                                                                <div className="sfi__items-empty">No line items on this invoice.</div>
                                                            ) : (
                                                                <div className="sfi__items">
                                                                    <div className="sfi__items-title">Line Items</div>
                                                                    <table className="sfi__items-table">
                                                                        <thead>
                                                                            <tr>
                                                                                <th>Fee Head</th>
                                                                                <th>Description</th>
                                                                                <th className="sfi__items-amt">Gross</th>
                                                                                <th className="sfi__items-amt">Discount</th>
                                                                                <th className="sfi__items-amt">Net</th>
                                                                            </tr>
                                                                        </thead>
                                                                        <tbody>
                                                                            {items.map((it) => {
                                                                                const disc = parseFloat(it.discount_amount);
                                                                                return (
                                                                                    <tr key={it.id}>
                                                                                        <td>
                                                                                            <span className="sfi__fee-head">{it.fee_head?.name ?? '—'}</span>
                                                                                            {it.is_optional && <span className="sfi__opt-badge">Optional</span>}
                                                                                        </td>
                                                                                        <td className="sfi__items-desc">{it.description ?? '—'}</td>
                                                                                        <td className="sfi__items-amt">{formatCurrency(parseFloat(it.gross_amount))}</td>
                                                                                        <td className="sfi__items-amt sfi__items-disc">{disc > 0 ? '−' + formatCurrency(disc) : '—'}</td>
                                                                                        <td className="sfi__items-amt sfi__items-net">{formatCurrency(parseFloat(it.net_amount))}</td>
                                                                                    </tr>
                                                                                );
                                                                            })}
                                                                        </tbody>
                                                                    </table>
                                                                </div>
                                                            )}
                                                        </td>
                                                    </tr>
                                                )}
                                            </Fragment>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>

                {!loading && meta.last_page > 1 && (
                    <div className="sfi__pagination">
                        <button className="sfi__page-btn" disabled={meta.current_page <= 1} onClick={() => setPage((p) => p - 1)}>‹ Prev</button>
                        <span className="sfi__page-info">Page {meta.current_page} of {meta.last_page}</span>
                        <button className="sfi__page-btn" disabled={meta.current_page >= meta.last_page} onClick={() => setPage((p) => p + 1)}>Next ›</button>
                    </div>
                )}
            </div>
        </>
    );
}
